import { BehaviorSubject, Subject, queueScheduler, scheduled } from 'rxjs';
import { Chain, OrderItem } from '@shared/data-access/interfaces';
/* eslint-disable max-len */
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { PurchaseItem, SameDayGroup, ShippingGroup, SortedBopisItemGroup } from '@my-account/data-access/interfaces';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { MyAccountAnalyticsService, Store } from '@shared/data-access/services';
import { UtilityService } from '@shared/utils/services';
import { first, } from 'rxjs/operators';

@Component({
	selector: 'my-account-account-summary-components-purchase-list-v2',
	templateUrl: './purchase-list-v2.component.html',
	styleUrls: ['./purchase-list-v2.component.scss']
})

export class PurchaseListV2Component implements OnInit {
	@ViewChild('checkinComponent') checkinComponent: ElementRef;

	@Input()
		resetCheckIn: BehaviorSubject<boolean>;

	@Input()
	// 0 = in-store purchase, 1 = online purchase
		purchaseType: number;

	@Input()
	// passed to TrackOrderService.getOrder
		lastPurchaseNumber?: number;

	@Input()
	// This will contain the data for an in-store purchase (can't access order details)
		mostRecentPurchase?: PurchaseItem;

	@Input()
	// this will contain the details of the  purchase item.
	orderDetail?: any;

	@Input()
	// Show accordion expanded or collapsed at creation time
		expandMROPanel: boolean;

	orderCompleted = false;
	orderDetailLoaded = false;
	itemTotal = 0;
	orderZip: any;
	inStorePurchaseData: any;

	// order sorting buckets
	bopisItemsBucket: OrderItem[] = [];
	sthItemsBucket: OrderItem[] = [];
	sameDayDeliveryItemsBucket: OrderItem[] = [];
	tempSortedShippingOrder: OrderItem[] = [];
	tempSortedBopisItems: OrderItem[] = [];
	sortedActiveBopisItems: SortedBopisItemGroup[] = [];
	sortedShippingGroups: ShippingGroup[] = [];
	sortedSameDayShippingGroups: SameDayGroup[] = [];
	cancelledShippingItems: OrderItem[] = [];
	cancelledBopisItems: OrderItem[] = [];
	cancelledSameDayDeliveryItems: OrderItem[] = [];
	noTrackItems: OrderItem[] = [];
	trackingItems: OrderItem[] = [];
	sddHasTracking: OrderItem[] = [];
	sddNoTracking: OrderItem[] = [];
	inStoreDetailsEnabled: boolean;
	private ngUnsubscribe: Subject<void> = new Subject();

	constructor(
		private myAccountAnalyticsService: MyAccountAnalyticsService,
		private store: Store,
		private utilityService: UtilityService,
		private chain: Chain) { }

	ngOnInit(): void {
		scheduled([this.getKillswitchValues()], queueScheduler);
		this.getLastPurchaseData();
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	getLastPurchaseData(){
		if (this.mostRecentPurchase && this.purchaseType === 0 && this.orderDetail) {
			if(!this.inStoreDetailsEnabled){
				this.inStorePurchaseData = this.mostRecentPurchase;
			} else{
				this.inStorePurchaseData = this.orderDetail;
				this.inStorePurchaseData.storeInfo = this.mostRecentPurchase.storeInfo;
				this.inStorePurchaseData.storeChain = this.mostRecentPurchase.storeChain;
				this.orderDetailLoaded = true;
				this.itemTotal = this.inStorePurchaseData.items.length;
			}
		}

		if (this.lastPurchaseNumber && this.purchaseType === 1 &&  this.orderDetail) {
			this.orderDetailLoaded = true;
			for (const item of this.orderDetail.items) {
				if (item.status !== 'Cancelled') {
					this.itemTotal++;
				}
			}
			this.sortOrderByFulfilmentMethod(this.orderDetail.items);
			this.sortedShippingGroups = this.sortShippingItems(this.sthItemsBucket);
			this.tempSortedBopisItems = this.sortBopisItemsByPickupLocation(this.bopisItemsBucket);
			this.sortBopisItems(this.bopisItemsBucket);
			this.sortedSameDayShippingGroups = this.sortSameDayItems(this.sameDayDeliveryItemsBucket);
			this.orderZip = this.orderDetail.zipCode;
		}
	}

	/**
	 * breaks items into three preliminary buckets: bopis items, active shipping, cancelled shipping
	 * @param items the items input from the order detail template.
	 */
	 public sortOrderByFulfilmentMethod(items: OrderItem[]): void {
		for (const item of items) {
			if (item.bopisItem && item.status !== 'Cancelled') {
				this.bopisItemsBucket.push(item);
			}
			if (!item.bopisItem && item.status !== 'Cancelled' && !item.shipModeCode.toUpperCase().includes('SAMEDAY')) {
				this.sthItemsBucket.push(item);
			}
			if (item.status !== 'Cancelled' && item.shipModeCode.toUpperCase().includes('SAMEDAY')) {
				this.sameDayDeliveryItemsBucket.push(item);
			}
			if (!item.bopisItem && item.status === 'Cancelled' && !item.shipModeCode.toUpperCase().includes('SAMEDAY')) {
				this.cancelledShippingItems.push(item);
			}
			if (item.bopisItem && item.status === 'Cancelled') {
				this.cancelledBopisItems.push(item);
			}
			if (!item.bopisItem && item.status === 'Cancelled' && item.shipModeCode.toUpperCase().includes('SAMEDAY')) {
				this.cancelledSameDayDeliveryItems.push(item);
			}
		}
	}

	/**
	 * removes duplicates of the entered key
	 */
	 public reduceValues(items: OrderItem[], key: string): string[] {
		const temp = [];
		for (const item of items) {
			if (item[key]) {
				temp.push(item[key]);
			}
		}
		return [...new Set(temp)];
	}

	public sortShipItemsByEDD(items: OrderItem[]): OrderItem[] {
		return items.sort((a, b) => a.estimatedDelivery.localeCompare(b.estimatedDelivery));
	}

	/**
	 *
	 * @param items all items from the ship to home items bucket
	 * @returns Sorted array of shipping items by earliest estimated delivery date to latest
	 */
	public sortShippingItems(items) {
		const shippingGroups: ShippingGroup[] = [];
		const trackingNumbers = this.reduceValues(this.sthItemsBucket, 'trackingNumber');

		// counters for shipping items without a tracking number yet
		let ground = 0;
		let secondDay = 0;
		let nextDay = 0;
		let ltl = 0;

		// break up shipment items even further - an array of items with tracking and an arrray without
		items.forEach((item) => {
			if (item.trackingNumber === '') {
				this.noTrackItems.push(item);
			} else {
				this.trackingItems.push(item);
			}
		});

		trackingNumbers.forEach((trackingNum) => {
			shippingGroups.push({
				groupShipModeCode: undefined,
				groupTrackingNumber: trackingNum === 'NA' ? 'NO_TRACKING_AVAILABLE_YET' : trackingNum,
				items: [],
			});
		});


		// determine how many different shipment methods we've got for items w/o a tracking number
		this.noTrackItems.forEach((item) => {
			switch (item.shipModeCode.substring(0,3).toLowerCase()) {
				// ground parcel
				case 'gro': {
					ground++;
					break;
				}
				// 2nd day parcel
				case '2nd': {
					secondDay++;
					break;
				}
				// next day parcel
				case 'nex': {
					nextDay++;
					break;
				}
				// LTL
				case 'ltl': {
					ltl++;
					break;
				}
			}
		});

		// and create ShippingGroups for any of our shipmodes w/o tracking
		if (ground > 0) {
			shippingGroups.push({
				groupShipModeCode: 'Ground Parcel',
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: [],
			});
		}
		if (secondDay > 0) {
			shippingGroups.push({
				groupShipModeCode: '2nd Day Parcel',
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: [],
			});
		}
		if (nextDay > 0) {
			shippingGroups.push({
				groupShipModeCode: 'Next Day Parcel',
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: [],
			});
		}
		if (ltl > 0) {
			shippingGroups.push({
				groupShipModeCode: 'LTL',
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: [],
			});
		}

		// Shipping Grouping Revision
		// We're going to loop through shipping groups with a tracking number first, then ones that still don't have one.
		for (const item of this.trackingItems) {
			// get the placeholder image if one has not been returned for the item
			if (item.imageIdentifier === '') {
				item.imageIdentifier = 'IMAGE_NOT_FOUND';
			}
			if (item.status !== 'Cancelled' && item.status !== 'In Process') {
				for (const shipGroup of shippingGroups) {
					if (item.trackingNumber === shipGroup.groupTrackingNumber) {
						shipGroup.orderStatus = this.orderDetail.status;
						shipGroup.orderTrackingNumber = this.orderDetail.trackingNumber;
						shipGroup.orderTrackingUrl = this.orderDetail.trackingUrl;
						shipGroup.groupStatus = item.status;
						if (item.estimatedDelivery !== null) {
							shipGroup.groupEstimatedDelivery = item.estimatedDelivery;
						} else {
							shipGroup.groupEstimatedDelivery = '';
						}
						shipGroup.groupTrackingUrl = item.trackingUrl;
						shipGroup.groupTrackingNumber = item.trackingNumber;
						shipGroup.groupStartEstimatedDelivery = item.startEstimatedDelivery || '';
						shipGroup.groupShipModeCode = item.shipModeCode;
						shipGroup.groupDeliveryMethod = item.deliveryMethod;
						shipGroup.groupShippingAddress = item.shippingRecipient?.address?.address1 || '';
						shipGroup.groupShippingCity = item.shippingRecipient?.address?.city || '';
						shipGroup.groupShippingState = item.shippingRecipient?.address?.state || '';
						shipGroup.groupTrackingStatus = item.trackingStatus || 'Please check tracking link for more details';
						shipGroup.groupTrackingLocation = item.trackingLocation || 'Please check tracking link for more details';

						shipGroup.items.push(item);
						shipGroup.items = [...new Set(shipGroup.items)];

						if (shipGroup.orderStatus === 'Shipped' && shipGroup.groupStatus === 'Completed' && shipGroup?.groupTrackingLocation.trim() === ',') {
							shipGroup.groupStatusCode = 'In Process';
						} else if (shipGroup?.groupTrackingLocation.trim() !== ',' && (shipGroup.orderStatus === 'Completed' || shipGroup.orderStatus === 'Fulfilled' || shipGroup.orderStatus === 'In Fulfillment') && shipGroup.groupTrackingStatus !== 'Delivered') {
							shipGroup.groupStatusCode = 'Shipped';
						} else if (shipGroup.groupTrackingStatus === 'Delivered') {
							shipGroup.groupStatusCode = 'Delivered';
							this.orderCompleted = true;
						}
					}
				}
			}
		}

		for (const item of this.noTrackItems) {
			// this fixed an issue where item(s) without tracking were also being injected into shipping groups
			// that had tracking numbers.
			const shipGroupsNoTrack: ShippingGroup[] = [];
			shippingGroups.forEach((group) => {
				if (group.groupTrackingNumber === 'NO_TRACKING_AVAILABLE_YET') {
					shipGroupsNoTrack.push(group);
				}
			});

			for (const shipGroup of shipGroupsNoTrack) {
				// make sure items go into the correct ship mode bucket
				if (item.trackingNumber === '' && item.shipModeCode.substring(0,3) === shipGroup.groupShipModeCode.substring(0,3) && item.status !== 'Cancelled') {
					shipGroup.orderStatus = this.orderDetail.status;
					shipGroup.orderTrackingNumber = this.orderDetail.trackingNumber;
					shipGroup.orderTrackingUrl = this.orderDetail.trackingUrl;
					if (item.estimatedDelivery !== null) {
						shipGroup.groupEstimatedDelivery = item.estimatedDelivery;
					} else {
						shipGroup.groupEstimatedDelivery = '';
					}
					shipGroup.groupStartEstimatedDelivery = item.startEstimatedDelivery || '';
					shipGroup.groupTrackingUrl = item?.trackingUrl || '';
					shipGroup.groupShipModeCode = item.shipModeCode;
					shipGroup.groupDeliveryMethod = item.deliveryMethod;
					shipGroup.groupShippingAddress = item.shippingRecipient?.address?.address1 || '';
					shipGroup.groupShippingCity = item.shippingRecipient?.address?.city || '';
					shipGroup.groupShippingState = item.shippingRecipient?.address?.state || '';
					shipGroup.groupTrackingStatus = item.trackingStatus;
					shipGroup.groupTrackingLocation = item.trackingLocation;
					shipGroup.items.push(item);
					if (shipGroup.orderStatus === 'Order Placed' && shipGroup.orderTrackingNumber === null) {
						shipGroup.groupStatusCode = 'Order Placed';
					}
				}
			}
		}
		return shippingGroups.sort((a, b) => (a.items[0].estimatedDelivery > b.items[0].estimatedDelivery) ? 1 : -1 );
	}

	public sortBopisItems(items) {
		// 1. get reduced pickup stores (sort bopis bucket by store)
		// 2. create temp active bopis group and temp cancelled bopis group
		// 3. sort by store and then by status
		// 4. push to appropriate status group
		const stores = this.reduceValues(items, 'store');
		for (const store of stores) {
			const tempActiveBopisItems: SortedBopisItemGroup = {
				store,
				items: [],
				status: '',
			};
			for (const item of items) {
				if (item.imageIdentifier === '') {
					item.imageIdentifier = 'IMAGE_NOT_FOUND';
				}
				if (item.store === store && item.status !== 'Cancelled') {
					tempActiveBopisItems.store = this.storeNameReformat(item.store);
					tempActiveBopisItems.status = item.status;
					tempActiveBopisItems.estimatedDeliveryDate = item.estimatedDelivery;
					tempActiveBopisItems.startEstimatedDeliveryDate = item.startEstimatedDelivery;
					tempActiveBopisItems.locationChain = this.getBopisGroupChain(item?.shippingRecipient?.address?.address1); // e.g. 'dsg'
					// check for null or empty shippingRecipient
					if (item.deliveryMethod.toLowerCase() !== 'bopis' && item.deliveryMethod.toLowerCase() !== 'bopl' && item.shippingRecipient?.address?.address1 !== '' && item.shippingRecipient?.address?.address1 !== null) {
						tempActiveBopisItems.store = this.toTitleCase(item?.shippingRecipient?.address?.address2);
						tempActiveBopisItems.locationAddress = this.formatBopisAddress(
							item?.shippingRecipient?.address?.address3,
							item?.shippingRecipient?.address?.city,
							item?.shippingRecipient?.address?.state,
							item?.shippingRecipient?.address?.postalCode
						);
					} else {
						tempActiveBopisItems.locationAddress = `USE_NO_ADDRESS`;
					}
					tempActiveBopisItems.items.push(item);
					if (item.status === 'Picked Up') {
						this.orderCompleted = true;
					}
				}
			}
			this.sortedActiveBopisItems.push(tempActiveBopisItems);
		}
	}

	public sortBopisItemsByPickupLocation(items: OrderItem[]): OrderItem[] {
		return items.sort((a, b) => a.store.localeCompare(b.store));
	}

	/**
	 * @description
	 * creates array of same day group from order items
	 * and assigns correct display text based on tracking status
	 * @param items
	 * @returns items in same day groupings
	 */
	public sortSameDayItems(items: OrderItem[]): SameDayGroup[] {
		const sameDayGroups: SameDayGroup[] = [];
		const trackingNumbers = this.reduceValues(this.sameDayDeliveryItemsBucket, 'trackingNumber');

		// break up by tracking / no tracking
		items.forEach((item) => {
			if (item.trackingNumber === '') {
				this.sddNoTracking.push(item);
			} else {
				this.sddHasTracking.push(item);
			}
		});

		// create a SameDayGroup per unique tracking number
		trackingNumbers.forEach((trackingNum) => {
			sameDayGroups.push({
				groupTrackingNumber: trackingNum !== 'NA' ? trackingNum : 'NO_TRACKING_AVAILABLE_YET',
				items: []
			});
		});
		// create one group of items without a tracking number yet - they will be sorted into a group when a tracking number is available
		if (this.sddNoTracking.length > 0) {
			sameDayGroups.push({
				groupTrackingNumber: 'NO_TRACKING_AVAILABLE_YET',
				items: []
			});
		}

		// sort items with a tracking number into the correct SameDayGroup
		for (const item of this.sddHasTracking) {
			// get the placeholder image if one has not been returned for the item
			if (item.imageIdentifier === '') {
				item.imageIdentifier = 'IMAGE_NOT_FOUND';
			}
			for (const sddGroup of sameDayGroups) {
				if (item.trackingNumber === sddGroup.groupTrackingNumber) {

					sddGroup.items.push(item);
					sddGroup.groupStatusCode = item.status;
					sddGroup.groupTrackingUrl = item.trackingUrl;
					sddGroup.groupDistributionOrder = item?.orderTracking?.distributionOrder;
					sddGroup.groupHeaderText = this.getGroupHeaderText(item.status);
					sddGroup.groupEstimatedDelivery = item.estimatedDelivery;
				}
			}
		}

		// sort the remaining same day items
		for (const item of this.sddNoTracking) {
			if (item.imageIdentifier === '') {
				item.imageIdentifier = 'IMAGE_NOT_FOUND';
			}

			const sddGroupsNoTrack: SameDayGroup[] = [];
			sameDayGroups.forEach((group) => {
				if (group.groupTrackingNumber === 'NO_TRACKING_AVAILABLE_YET') {
					sddGroupsNoTrack.push(group);
				}
			});

			for (const sddGroup of sddGroupsNoTrack) {
				sddGroup.items.push(item);
				sddGroup.groupStatusCode = item.status;
				sddGroup.groupHeaderText = this.getGroupHeaderText(item.status);
				sddGroup.groupEstimatedDelivery = item.estimatedDelivery;
			}
		}
		return sameDayGroups;
	}

	/**
	 * matches the appropriate ui display group header text with the tracking status
	 * @param trackingStatus
	 * @returns group header text
	 */
	public getGroupHeaderText(status: string): string {
		switch (status) {
			case 'Preparing Delivery': {
				return 'Same Day Delivery';
			}
			case 'On the Way': {
				return 'On the Way with DoorDash';
			}
			case 'Delivered': {
				return 'Delivered by DoorDash';
			}
			case 'Undeliverable': {
				return 'Undeliverable';
			}
			case 'Preparing for Shipment': {
				return 'Preparing';
			}
			default: {
				return '';
			}
		}
	}

	/**
	 * Reformat Store Name from Item data. Content is passed as all-caps
	 * @param storeName the item.store value on a bopis order item. e.g. 'DICKS SPORTING GOODS - ROBINSON TOWN CENTRE'
	 * @returns a reformatted string value
	 */
	 public storeNameReformat(storeName: string): string {
		const regDSG = /^D/ig;
		const regGG = /^GOL/ig;
		const regG3 = /^GOI/ig;
		const regPL = /^P/ig;
		const orderChain = storeName.split('-')[0].trim(); // e.g. 'DICKS SPORTING GOODS'
		const storeLocation = storeName.split('-')[1].trim(); // e.g. 'ROBINSON TOWN CENTRE'
		if (regDSG.test(orderChain)) {
			return `DICK'S Sporting Goods - ${storeLocation}`;
		} else if (regGG.test(orderChain)) {
			return `Golf Galaxy - ${storeLocation}`;
		} else if (regG3.test(orderChain)) {
			return `Going Going Gone - ${storeLocation}`;
		} else if (regPL.test(orderChain)) {
			return `Public Lands - ${storeLocation}`;
		} else {
			// something unexpected comes through? Just return the unaltered store value
			return storeName;
		}
	}

	/**
	 * Determine the chain for a BOPIS/BOPL item
	 * @param storeName
	 * @returns chain abbreviation as string
	 */
	 public getBopisGroupChain(storeName: string): string {
		const regDSG = /^D/ig;
		const regGG = /^GOL/ig;
		const regG3 = /^GOI/ig;
		const regPL = /^P/ig;
		const orderChain = storeName.split('-')[0].trim(); // e.g. 'DICKS SPORTING GOODS'

		if (regDSG.test(orderChain)) {
			return 'dsg';
		} else if (regGG.test(orderChain)) {
			return 'gg';
		} else if (regG3.test(orderChain)) {
			return 'ggg';
		} else if (regPL.test(orderChain)) {
			return 'pl';
		}
	}

	/**
	 * Determine and reformat street address for BOPIS/BOPL location
	 * @param addr
	 * @param city
	 * @param state
	 * @param zip
	 * @returns single string concatenated with address converted to title case
	 */
	 public formatBopisAddress(addr: string, city: string, state: string, zip: string): string {
		return `${this.toTitleCase(addr)} ${this.toTitleCase(city)}, ${state} ${zip}`;
	}

	public toTitleCase(str: string): string {
		return str.split(' ').map(w => w[0]?.toUpperCase() + w.substring(1).toLowerCase()).join(' ');
	}

	public analyticsClick(orderNumber: string, eventType: string, eventName: string): void {
		this.myAccountAnalyticsService.setEventType(eventType);
		this.myAccountAnalyticsService.setOrderId(orderNumber);
		this.myAccountAnalyticsService.triggerAnalyticsEvent(eventName);
	}

	handleImageError(imgEl: any, requestedImg: string) {
		imgEl.src = DCSG_CONSTANTS.imageUnavailable;
		this.myAccountAnalyticsService.emitMissingImageEvent({
			pageUrl: window.location.href,
			requestedImg: requestedImg
		});
	}

	private getKillswitchValues(): void {
		this.store
			.select('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(first())
			.subscribe((killswitch: any) => {
				this.inStoreDetailsEnabled = this.utilityService.checkKillSwitch('inStoreDetailsEnabled', killswitch, this.chain.chainIdentifierAbbr);
			});
	}
}
